import { useState, useEffect, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

const scrollNavItems = [
  { label: "Home", id: "home" },
  { label: "Story", id: "story" },
  { label: "Wildfire", id: "wildfire" },
  { label: "Tech", id: "how-it-works" },
  { label: "Stats", id: "about" },
  { label: "Eco", id: "sustainability" },
  { label: "About", id: "products" },
  { label: "What Sets Us Apart", id: "benefits" },
  { label: "Reviews", id: "reviews" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeId, setActiveId] = useState("home");
  const location = useLocation();
  const navigate = useNavigate();
  const isHomePage = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Track which section is in view (only on home page)
  useEffect(() => {
    if (!isHomePage) return;

    const allIds = [...scrollNavItems.map((item) => item.id), "models"];
    const sectionEls = allIds
      .map((id) => document.getElementById(id))
      .filter(Boolean) as HTMLElement[];

    if (sectionEls.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length > 0) {
          setActiveId(visible[0].target.id);
        }
      },
      {
        rootMargin: "-20% 0px -60% 0px",
        threshold: 0,
      }
    );

    sectionEls.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [isHomePage]);

  const scrollTo = useCallback((id: string) => {
    setMobileOpen(false);
    const el = document.getElementById(id);
    if (el) {
      const headerOffset = 80;
      const top = el.getBoundingClientRect().top + window.scrollY - headerOffset;
      window.scrollTo({ top, behavior: "smooth" });
    }
  }, []);

  const getLinkClass = (id: string) => {
    const isActive = activeId === id;
    if (isActive) {
      return "relative text-brand font-bold transition-colors duration-200 after:absolute after:bottom-[-3px] after:left-0 after:right-0 after:h-[2px] after:rounded-full after:bg-brand after:content-['']";
    }
    return "relative text-gray-600 hover:text-gray-900 font-semibold transition-colors duration-200 after:absolute after:bottom-[-3px] after:left-0 after:right-0 after:h-[2px] after:rounded-full after:bg-brand after:scale-x-0 after:origin-left hover:after:scale-x-100 after:transition-transform after:duration-200 after:content-['']";
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/95 backdrop-blur-md shadow-sm border-b border-gray-200"
          : "bg-transparent"
      }`}
    >
      <div className="mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="flex items-center h-16">
          {/* Logo */}
          <button
            onClick={() => scrollTo("home")}
            className="flex items-center gap-3 sm:gap-4 group py-2 cursor-pointer shrink-0"
          >
            <span className="text-2xl sm:text-3xl font-extrabold tracking-tight leading-none transition-colors duration-200 group-hover:opacity-80 whitespace-nowrap text-gray-900">
              Airdog
            </span>
            <div className="hidden sm:block lg:hidden w-px h-6 bg-gray-300/60" />
            <span className="hidden lg:hidden text-base sm:text-lg font-extrabold tracking-[0.12em] uppercase leading-none transition-all duration-300 group-hover:opacity-80 whitespace-nowrap bg-gradient-to-r from-teal-500 via-emerald-500 to-teal-400 bg-clip-text text-transparent">
              World's Best Air
            </span>
          </button>

          {/* Centered tagline — between logo and nav */}
          <span className="hidden lg:block flex-1 text-center text-xl lg:text-2xl font-extrabold tracking-[0.12em] uppercase leading-none bg-gradient-to-r from-teal-500 via-emerald-500 to-teal-400 bg-clip-text text-transparent pointer-events-none select-none whitespace-nowrap">
            World's Best Air
          </span>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-3 shrink-0">
            {scrollNavItems.map((item) => (
              item.href ? (
                <Link
                  key={item.id}
                  to={item.href}
                  className={`${getLinkClass(item.id)} whitespace-nowrap cursor-pointer text-xs px-2 py-1`}
                  onClick={() => setMobileOpen(false)}
                >
                  {item.label}
                </Link>
              ) : (
                <button
                  key={item.id}
                  onClick={() => {
                    if (isHomePage) {
                      scrollTo(item.id);
                    } else {
                      navigate(`/#${item.id}`);
                    }
                  }}
                  className={`${getLinkClass(item.id)} whitespace-nowrap cursor-pointer text-xs px-2 py-1`}
                >
                  {item.label}
                </button>
              )
            ))}
            <Link
              to="/models"
              className={`${getLinkClass("models")} whitespace-nowrap cursor-pointer text-xs px-2 py-1`}
              onClick={() => setMobileOpen(false)}
            >
              Models
            </Link>
          </nav>

          {/* Mobile hamburger */}
          <button
            className="lg:hidden p-2 text-gray-900 hover:text-gray-600 transition-colors cursor-pointer"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
          >
            <i className={`ri-${mobileOpen ? "close" : "menu"}-line text-xl`} />
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="lg:hidden bg-white border-t border-gray-200 shadow-lg">
          <div className="px-4 py-4 space-y-1">
            {scrollNavItems.map((item) => {
              const isActive = activeId === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => {
                    if (item.href) {
                      navigate(item.href);
                      setMobileOpen(false);
                    } else if (isHomePage) {
                      scrollTo(item.id);
                    } else {
                      navigate(`/#${item.id}`);
                    }
                  }}
                  className={`flex items-center gap-3 w-full text-left py-2.5 px-3 rounded-lg transition-all duration-200 cursor-pointer ${
                    isActive
                      ? "bg-brand/8 text-brand font-bold"
                      : "text-gray-700 hover:bg-gray-50 hover:text-gray-900 font-semibold"
                  }`}
                >
                  {isActive && (
                    <span className="w-1.5 h-1.5 rounded-full bg-brand shrink-0" />
                  )}
                  {item.label}
                </button>
              );
            })}
            <Link
              to="/models"
              onClick={() => setMobileOpen(false)}
              className={`flex items-center gap-3 w-full text-left py-2.5 px-3 rounded-lg transition-all duration-200 cursor-pointer ${
                activeId === "models"
                  ? "bg-brand/8 text-brand font-bold"
                  : "text-gray-700 hover:bg-gray-50 hover:text-gray-900 font-semibold"
              }`}
            >
              {activeId === "models" && (
                <span className="w-1.5 h-1.5 rounded-full bg-brand shrink-0" />
              )}
              Models
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}